import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const ALL_SEASONS = "__all__";

const pct = (m, a) => (a > 0 ? ((m / a) * 100).toFixed(1) : "—");
const points = (r) => 2 * (r.fg2_made || 0) + 3 * (r.fg3_made || 0) + (r.ft_made || 0);
const tfgm = (r) => (r.fg2_made || 0) + (r.fg3_made || 0);
const tfga = (r) => (r.fg2_att || 0) + (r.fg3_att || 0);

// Columns match the coach's Excel sheet exactly
const COLS = [
  { key: "opp",    label: "OPPONENT",   w: "min-w-[180px] text-left sticky left-0 bg-ink-900 z-10" },
  { key: "gp",     label: "GP",         w: "w-12"  },
  { key: "fg2m",   label: "2FGM",       w: "w-14"  },
  { key: "fg2a",   label: "2FGA",       w: "w-14"  },
  { key: "fg2p",   label: "%",          w: "w-16 text-ink-300"  },
  { key: "fg3m",   label: "3FGM",       w: "w-14"  },
  { key: "fg3a",   label: "3FGA",       w: "w-14"  },
  { key: "fg3p",   label: "%",          w: "w-16 text-ink-300"  },
  { key: "tfgm",   label: "TFGM",       w: "w-14"  },
  { key: "tfga",   label: "TFGA",       w: "w-14"  },
  { key: "tfgp",   label: "TFG%",       w: "w-16 text-ink-300"  },
  { key: "ftm",    label: "FTM",        w: "w-14"  },
  { key: "fta",    label: "FTA",        w: "w-14"  },
  { key: "ftp",    label: "%",          w: "w-16 text-ink-300" },
  { key: "tp",     label: "TP",         w: "w-14 text-burgundy font-black" },
  { key: "avg_p",  label: "AVG",        w: "w-14 text-ink-300" },
  { key: "reb",    label: "REB",        w: "w-14" },
  { key: "avg_r",  label: "AVG",        w: "w-14 text-ink-300" },
  { key: "ast",    label: "AST",        w: "w-14" },
  { key: "avg_a",  label: "AVG",        w: "w-14 text-ink-300" },
  { key: "stl",    label: "STL",        w: "w-14" },
  { key: "blk",    label: "BLK",        w: "w-14" },
  { key: "to",     label: "TO",         w: "w-14" },
];

function opponentLabel(g) {
  if (!g) return "—";
  return `${g.opponent}${g.home_away === "away" ? " (A)" : ""}${g.playoff ? " · Playoff" : ""}`;
}

function rowValues(g) {
  const p = points(g);
  const m = tfgm(g), a = tfga(g);
  return {
    gp:    1,
    fg2m:  g.fg2_made || 0,
    fg2a:  g.fg2_att  || 0,
    fg2p:  pct(g.fg2_made, g.fg2_att),
    fg3m:  g.fg3_made || 0,
    fg3a:  g.fg3_att  || 0,
    fg3p:  pct(g.fg3_made, g.fg3_att),
    tfgm:  m,
    tfga:  a,
    tfgp:  pct(m, a),
    ftm:   g.ft_made || 0,
    fta:   g.ft_att  || 0,
    ftp:   pct(g.ft_made, g.ft_att),
    tp:    p,
    avg_p: p.toString(),  // per-game view: same as TP
    reb:   g.rebounds || 0,
    avg_r: (g.rebounds || 0).toString(),
    ast:   g.assists || 0,
    avg_a: (g.assists || 0).toString(),
    stl:   g.steals || 0,
    blk:   g.blocks || 0,
    to:    g.turnovers || 0,
  };
}

function totalsValues(totals) {
  const p = 2 * totals.fg2_made + 3 * totals.fg3_made + totals.ft_made;
  const m = totals.fg2_made + totals.fg3_made;
  const a = totals.fg2_att + totals.fg3_att;
  const gp = totals.gp || 1;
  return {
    gp:    totals.gp,
    fg2m:  totals.fg2_made,
    fg2a:  totals.fg2_att,
    fg2p:  pct(totals.fg2_made, totals.fg2_att),
    fg3m:  totals.fg3_made,
    fg3a:  totals.fg3_att,
    fg3p:  pct(totals.fg3_made, totals.fg3_att),
    tfgm:  m,
    tfga:  a,
    tfgp:  pct(m, a),
    ftm:   totals.ft_made,
    fta:   totals.ft_att,
    ftp:   pct(totals.ft_made, totals.ft_att),
    tp:    p,
    avg_p: (p / gp).toFixed(1),
    reb:   totals.rebounds,
    avg_r: (totals.rebounds / gp).toFixed(1),
    ast:   totals.assists,
    avg_a: (totals.assists / gp).toFixed(1),
    stl:   totals.steals,
    blk:   totals.blocks,
    to:    totals.turnovers,
  };
}

export default function PlayerCareer() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(ALL_SEASONS);

  useEffect(() => {
    api.get(`/player-career/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e)));
  }, [slug]);

  const filtered = useMemo(() => {
    if (!data) return null;
    if (selectedSeason === ALL_SEASONS) return data.by_season; // grouped
    return data.by_season.filter((b) => b.season === selectedSeason);
  }, [data, selectedSeason]);

  const exportExcel = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Player", data.player.player_name, "Jersey", data.player.jersey_number || "", "Position", data.player.position || ""]);
    rows.push([]);
    for (const group of data.by_season) {
      rows.push([`SEASON ${group.season}`]);
      rows.push(["OPPONENT", "GP", "2FGM", "2FGA", "2FG%", "3FGM", "3FGA", "3FG%", "TFGM", "TFGA", "TFG%", "FTM", "FTA", "FT%", "TP", "AVG", "REB", "AVG", "AST", "AVG", "STL", "BLK", "TO"]);
      for (const g of group.games) {
        const v = rowValues(g);
        rows.push([opponentLabel(g), v.gp, v.fg2m, v.fg2a, v.fg2p, v.fg3m, v.fg3a, v.fg3p, v.tfgm, v.tfga, v.tfgp, v.ftm, v.fta, v.ftp, v.tp, v.avg_p, v.reb, v.avg_r, v.ast, v.avg_a, v.stl, v.blk, v.to]);
      }
      const t = totalsValues(group.totals);
      rows.push([`SEASON ${group.season} TOTALS`, t.gp, t.fg2m, t.fg2a, t.fg2p, t.fg3m, t.fg3a, t.fg3p, t.tfgm, t.tfga, t.tfgp, t.ftm, t.fta, t.ftp, t.tp, t.avg_p, t.reb, t.avg_r, t.ast, t.avg_a, t.stl, t.blk, t.to]);
      rows.push([]);
    }
    const c = totalsValues(data.career_totals);
    rows.push(["CAREER TOTALS", c.gp, c.fg2m, c.fg2a, c.fg2p, c.fg3m, c.fg3a, c.fg3p, c.tfgm, c.tfga, c.tfgp, c.ftm, c.fta, c.ftp, c.tp, c.avg_p, c.reb, c.avg_r, c.ast, c.avg_a, c.stl, c.blk, c.to]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Career");
    XLSX.writeFile(wb, `${data.player.player_name.replace(/\s+/g, "_")}_career.xlsx`);
    toast.success("Career export downloaded");
  };

  if (!data) return <div className="p-10 text-ink-300">Loading career…</div>;

  const { player, career_totals, seasons } = data;
  const noStats = !seasons || seasons.length === 0;

  return (
    <div className="p-6 md:p-10" data-testid={`career-${slug}`}>
      {/* Header */}
      <div className="mb-6">
        <Link to={`/player/${slug}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-ink-300 hover:text-white transition mb-4" data-testid="career-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to profile
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <PlayerAvatar player={player} size="lg" />
            <div>
              <p className="eyebrow">Career Log · Game-by-Game</p>
              <h1 className="heading-1">{player.player_name}</h1>
              <p className="text-ink-300 text-sm mt-1">
                NPHS Canucks · #{player.jersey_number || "—"} · {player.position || "—"}
                {player.status !== "active" && player.graduation_year && (
                  <span className="ml-2 text-amber-300">Alumni &apos;{String(player.graduation_year).slice(-2)}</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-ink-400">Season</span>
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger className="w-[180px] bg-ink-900 border-ink-700 text-white font-display font-bold uppercase tracking-wider h-9" data-testid="career-season-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-ink-900 border-ink-700">
                  <SelectItem value={ALL_SEASONS} className="text-white">All Seasons</SelectItem>
                  {seasons.map((s) => (
                    <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {user && (
              <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={exportExcel} data-testid="career-export-btn" disabled={noStats}>
                <Download className="w-4 h-4 mr-2" /> Export Excel
              </Button>
            )}
          </div>
        </div>
      </div>

      {noStats && (
        <div className="bg-ink-800 rounded-lg border border-white/5 p-8 text-center text-ink-300" data-testid="career-empty">
          No games logged yet for this player. Once stats are entered, they&apos;ll appear here grouped by season.
        </div>
      )}

      {!noStats && (
        <div className="space-y-8">
          {filtered.map((group) => (
            <SeasonBlock key={group.season} group={group} />
          ))}

          {/* Career totals — only when viewing all seasons */}
          {selectedSeason === ALL_SEASONS && data.by_season.length > 1 && (
            <div className="bg-burgundy/10 border-2 border-burgundy/60 rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-burgundy/40 bg-burgundy/20">
                <p className="eyebrow text-white">Career Totals · All Seasons</p>
              </div>
              <StatTable
                headerAccent
                totalsRow={totalsValues(career_totals)}
                totalsLabel="CAREER TOTAL"
                games={[]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SeasonBlock({ group }) {
  const t = totalsValues(group.totals);
  return (
    <div className="bg-ink-800 rounded-lg border border-white/5 overflow-hidden" data-testid={`career-season-${group.season}`}>
      <div className="px-5 py-3 border-b border-ink-700/60 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="eyebrow">Season</p>
          <h2 className="font-display text-2xl font-black uppercase tracking-tight">{group.season}</h2>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <StatPill label="GP" value={t.gp} />
          <StatPill label="PPG" value={t.avg_p} accent />
          <StatPill label="RPG" value={t.avg_r} />
          <StatPill label="APG" value={t.avg_a} />
        </div>
      </div>
      <StatTable games={group.games} totalsRow={t} totalsLabel={`${group.season} TOTAL`} />
    </div>
  );
}

function StatPill({ label, value, accent }) {
  return (
    <div className="text-center">
      <p className="text-[9px] uppercase tracking-wider text-ink-400">{label}</p>
      <p className={`font-display font-black text-lg ${accent ? "text-burgundy" : "text-white"}`}>{value}</p>
    </div>
  );
}

function StatTable({ games, totalsRow, totalsLabel, headerAccent }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs" data-testid="career-table">
        <thead>
          <tr className={`${headerAccent ? "bg-burgundy/20" : "bg-ink-900"} text-ink-300 uppercase tracking-wider`}>
            {COLS.map((c) => (
              <th key={c.key} className={`px-2 py-2 text-center font-semibold ${c.w}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {games.map((g, idx) => {
            const v = rowValues(g);
            return (
              <tr key={`${g.game_id}-${idx}`} className={`border-t border-ink-700/40 ${idx % 2 === 1 ? "bg-ink-900/30" : ""} hover:bg-ink-700/30 transition`}>
                <td className="px-3 py-2 text-left sticky left-0 bg-inherit font-semibold text-white truncate max-w-[240px]">
                  <Link to={`/game/${g.game_id}`} className="hover:text-burgundy transition" title={`${g.date} · ${g.opponent}`}>
                    {opponentLabel(g)}
                  </Link>
                  <p className="text-[10px] text-ink-400 font-normal">{g.date}</p>
                </td>
                <td className="px-2 py-2 text-center">{v.gp}</td>
                <td className="px-2 py-2 text-center">{v.fg2m}</td>
                <td className="px-2 py-2 text-center">{v.fg2a}</td>
                <td className="px-2 py-2 text-center text-ink-300">{v.fg2p}</td>
                <td className="px-2 py-2 text-center">{v.fg3m}</td>
                <td className="px-2 py-2 text-center">{v.fg3a}</td>
                <td className="px-2 py-2 text-center text-ink-300">{v.fg3p}</td>
                <td className="px-2 py-2 text-center">{v.tfgm}</td>
                <td className="px-2 py-2 text-center">{v.tfga}</td>
                <td className="px-2 py-2 text-center text-ink-300">{v.tfgp}</td>
                <td className="px-2 py-2 text-center">{v.ftm}</td>
                <td className="px-2 py-2 text-center">{v.fta}</td>
                <td className="px-2 py-2 text-center text-ink-300">{v.ftp}</td>
                <td className="px-2 py-2 text-center text-burgundy font-black">{v.tp}</td>
                <td className="px-2 py-2 text-center text-ink-300">—</td>
                <td className="px-2 py-2 text-center">{v.reb}</td>
                <td className="px-2 py-2 text-center text-ink-300">—</td>
                <td className="px-2 py-2 text-center">{v.ast}</td>
                <td className="px-2 py-2 text-center text-ink-300">—</td>
                <td className="px-2 py-2 text-center">{v.stl}</td>
                <td className="px-2 py-2 text-center">{v.blk}</td>
                <td className="px-2 py-2 text-center">{v.to}</td>
              </tr>
            );
          })}
          {/* Totals row */}
          <tr className="bg-burgundy/25 border-t-2 border-burgundy font-bold" data-testid="career-totals-row">
            <td className="px-3 py-2.5 text-left sticky left-0 bg-burgundy/25 font-display uppercase tracking-wider text-white">{totalsLabel}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.gp}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fg2m}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fg2a}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fg2p}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fg3m}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fg3a}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fg3p}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.tfgm}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.tfga}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.tfgp}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.ftm}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.fta}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.ftp}</td>
            <td className="px-2 py-2.5 text-center text-burgundy font-black">{totalsRow.tp}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.avg_p}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.reb}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.avg_r}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.ast}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.avg_a}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.stl}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.blk}</td>
            <td className="px-2 py-2.5 text-center text-white">{totalsRow.to}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
